<template>
	<div class="wrapper">
		<div v-if="loading" class="text-center">
			<b-spinner label="Loading..." style="width: 3rem; height: 3rem;" variant="info"></b-spinner>
		</div>

		<!-- ═══ Result — replaces the form once the client exists ═══════════════ -->
		<div v-else-if="result" class="animated fadeIn">
			<b-row>
				<b-col cols="12" lg="9" xl="8">
					<b-card header-tag="header">
						<div slot="header">
							<i class="fa fa-check-circle text-success"></i>
							{{ result.officeCreated === false ? 'Device Portal project created' : 'Client created' }}
						</div>

						<b-alert v-for="(warning, i) in result.warnings" :key="i" variant="warning" show>
							{{ warning }}
						</b-alert>

						<table class="table table-sm handover-table">
							<tbody>
								<tr>
									<th>Client</th>
									<td>{{ submitted.clientName }}</td>
								</tr>
								<tr v-if="result.portalProjectCreated">
									<th>Device Portal project</th>
									<td>
										<code>{{ result.projectCode }}</code>
										<span class="text-muted"> &nbsp;(id {{ result.projectId }})</span>
									</td>
								</tr>
								<tr v-else>
									<th>Device Portal project</th>
									<td>Existing project <span class="text-muted">(id {{ result.projectId }})</span></td>
								</tr>
								<tr v-if="result.officeCreated !== false">
									<th>Tipsoi office</th>
									<td>#{{ result.officeId }}</td>
								</tr>
								<tr v-else>
									<th>Tipsoi office</th>
									<td class="text-muted">Not created — create one later from this project</td>
								</tr>
								<tr>
									<th>Login email</th>
									<td>
										{{ result.adminEmail }}
										<small class="text-muted d-block">Device Portal username and Tipsoi login</small>
									</td>
								</tr>
								<tr>
									<th>Password</th>
									<td>
										<span v-if="showPassword"><code>{{ submitted.adminPassword }}</code></span>
										<span v-else>••••••••</span>
										<b-button size="sm" variant="link" class="p-0 ml-2" @click="showPassword = !showPassword">
											{{ showPassword ? 'hide' : 'show' }}
										</b-button>
									</td>
								</tr>
								<tr>
									<th>Tipsoi Pay</th>
									<td>{{ result.billingStatus || 'Not requested' }}</td>
								</tr>
							</tbody>
						</table>

						<div class="form-actions">
							<b-button variant="primary" @click="copyHandover">
								<i class="fa fa-clipboard"></i> Copy handover details
							</b-button>
							&nbsp;
							<b-button variant="secondary" @click="startAnother">
								<i class="fa fa-plus"></i> Create another
							</b-button>
						</div>
					</b-card>
				</b-col>
			</b-row>
		</div>

		<!-- ═══ Form ═══════════════════════════════════════════════════════════ -->
		<div v-else class="animated fadeIn">
			<b-row>
				<b-col cols="12">
					<b-card header-tag="header">
						<div slot="header">
							<i class="icon-user-follow"></i> Create Client
							<small class="text-muted ml-2">Device Portal project, Tipsoi office and admin login in one step</small>
						</div>

						<b-form @submit.prevent="checkFormValidity()" method="post" autocomplete="off">

							<!-- ── What to create ─────────────────────────────────── -->
							<b-card border-variant="info" class="mb-3">
								<div slot="header" class="font-weight-bold">What should this create?</div>

								<b-form-radio-group v-model="form.mode" stacked @change="onModeChange">
									<b-form-radio value="PROJECT_AND_OFFICE">
										<strong>Device Portal project + Tipsoi office</strong>
										<small class="d-block text-muted">
											The full onboarding: a new Device Portal project, the Tipsoi office and the
											admin login, in one step.
										</small>
									</b-form-radio>
									<b-form-radio value="PROJECT_ONLY" class="mt-2">
										<strong>Device Portal project only</strong>
										<small class="d-block text-muted">
											Just the portal project — the same as Create Project, but with the code
											generated for you. No Tipsoi office is created.
										</small>
									</b-form-radio>
									<b-form-radio value="OFFICE_ON_EXISTING_PROJECT" class="mt-2">
										<strong>Tipsoi office on an existing project</strong>
										<small class="d-block text-muted">
											Attach a new office to a Device Portal project that already exists.
										</small>
									</b-form-radio>
								</b-form-radio-group>

								<b-form-group
									v-if="form.mode === 'OFFICE_ON_EXISTING_PROJECT'"
									label="Select Project"
									label-for="project-id"
									label-class="required"
									:label-cols="3"
									:horizontal="true"
									class="mt-3"
									invalid-feedback="Please select one."
									:state="$v.form.projectId.$dirty ? !$v.form.projectId.$error : null">
									<model-select
										id="project-id"
										:options="projectDropDownList"
										v-model="form.projectId"
										:isError="$v.form.projectId.$dirty ? $v.form.projectId.$error : false"
										placeholder="Select an existing project">
									</model-select>
									<small class="form-text text-muted">
										<span v-if="projectsLoading">Loading projects…</span>
										<span v-else-if="projectsError" class="text-danger">{{ projectsError }}</span>
										<span v-else>The office will be attached to this existing Device Portal project.</span>
									</small>
								</b-form-group>

								<p v-if="createsPortalProject" class="mb-0 mt-3 text-muted">
									<strong>Code is generated automatically</strong> and no device type is needed —
									the portal username and password are the admin login below.
								</p>
							</b-card>

							<b-container fluid class="px-0">
								<b-row>
									<!-- ── Client ─────────────────────────────────── -->
									<b-col cols="12" md="6">
										<b-card header-tag="header" title="Client">
											<b-form-group
												label="Client / Organization"
												label-for="clientName"
												label-class="required"
												:label-cols="4"
												:horizontal="true">
												<b-form-input v-model="form.clientName" name="clientName" id="clientName" type="text"
													placeholder="e.g. Acme Industries Ltd"
													:state="$v.form.clientName.$dirty ? !$v.form.clientName.$error : null"
													aria-describedby="client-name-feedback"></b-form-input>
												<b-form-invalid-feedback id="client-name-feedback">This is a required field and must not be blank.</b-form-invalid-feedback>
												<small class="form-text text-muted">Used as the office name, company name and portal project name.</small>
											</b-form-group>

											<b-form-group
												label="Address"
												label-for="address"
												label-class="required"
												:label-cols="4"
												:horizontal="true">
												<b-form-textarea v-model="form.address" name="address" id="address" rows="3" max-rows="5"
													placeholder="Address"
													:state="$v.form.address.$dirty ? !$v.form.address.$error : null"
													aria-describedby="address-feedback"></b-form-textarea>
												<b-form-invalid-feedback id="address-feedback">This is a required field and must not be blank.</b-form-invalid-feedback>
											</b-form-group>

											<b-form-group
												label="Phone"
												label-for="phone"
												label-class="required"
												:label-cols="4"
												:horizontal="true">
												<b-form-input v-model="form.phone" name="phone" id="phone" type="text"
													placeholder="Phone"
													:state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
													aria-describedby="phone-feedback"></b-form-input>
												<b-form-invalid-feedback id="phone-feedback">This is a required field and must not be blank.</b-form-invalid-feedback>
											</b-form-group>
										</b-card>
									</b-col>

									<!-- ── Admin login ────────────────────────────── -->
									<b-col cols="12" md="6">
										<b-card header-tag="header" title="Admin Login">
											<p class="text-muted small">
												One credential pair — the same email and password sign in to both the
												Tipsoi Device Portal and the Tipsoi client panel.
											</p>

											<b-form-group
												label="Full name"
												label-for="adminName"
												label-class="required"
												:label-cols="4"
												:horizontal="true">
												<b-form-input v-model="form.adminName" name="adminName" id="adminName" type="text"
													placeholder="Admin name"
													:state="$v.form.adminName.$dirty ? !$v.form.adminName.$error : null"
													aria-describedby="admin-name-feedback"></b-form-input>
												<b-form-invalid-feedback id="admin-name-feedback">This is a required field and must not be blank.</b-form-invalid-feedback>
											</b-form-group>

											<b-form-group
												label="Email"
												label-for="adminEmail"
												label-class="required"
												:label-cols="4"
												:horizontal="true">
												<b-form-input v-model="form.adminEmail" name="adminEmail" id="adminEmail" type="text"
													placeholder="admin@client.com" autocomplete="off"
													@input="onEmailInput"
													:state="emailState"
													aria-describedby="admin-email-feedback"></b-form-input>
												<b-form-invalid-feedback id="admin-email-feedback">
													{{ emailTaken ? 'This email is already in use — choose another.' : 'A valid email is required.' }}
												</b-form-invalid-feedback>
												<small class="form-text" :class="emailChecking ? 'text-muted' : 'text-success'">
													<span v-if="emailChecking">Checking availability…</span>
													<span v-else-if="emailAvailable">✓ Available</span>
												</small>
											</b-form-group>

											<b-form-group
												label="Password"
												label-for="adminPassword"
												label-class="required"
												:label-cols="4"
												:horizontal="true">
												<b-input-group>
													<b-form-input v-model="form.adminPassword" name="adminPassword" id="adminPassword"
														:type="showPassword ? 'text' : 'password'"
														placeholder="Password" autocomplete="new-password"
														:state="$v.form.adminPassword.$dirty ? !$v.form.adminPassword.$error : null"></b-form-input>
													<b-input-group-append>
														<b-button variant="secondary" @click="generatePassword">Generate</b-button>
													</b-input-group-append>
												</b-input-group>
												<b-form-invalid-feedback :force-show="$v.form.adminPassword.$dirty && $v.form.adminPassword.$error">
													Required, and at least 6 characters.
												</b-form-invalid-feedback>
											</b-form-group>

											<b-form-group
												label="Confirm password"
												label-for="confirmPassword"
												label-class="required"
												:label-cols="4"
												:horizontal="true">
												<b-form-input v-model="form.confirmPassword" name="confirmPassword" id="confirmPassword"
													:type="showPassword ? 'text' : 'password'"
													placeholder="Confirm password" autocomplete="new-password"
													:state="$v.form.confirmPassword.$dirty ? !$v.form.confirmPassword.$error : null"
													aria-describedby="confirm-feedback"></b-form-input>
												<b-form-invalid-feedback id="confirm-feedback">Must match the password.</b-form-invalid-feedback>
												<b-form-checkbox v-model="showPassword" class="mt-2">Show passwords</b-form-checkbox>
											</b-form-group>
										</b-card>
									</b-col>
								</b-row>
							</b-container>

							<!-- ── Plan (office only) ─────────────────────────────── -->
							<b-card v-if="createsOffice" header-tag="header" title="Plan" class="mb-3">
								<b-row>
									<b-col cols="12" md="6">
										<b-form-group
											label="Version"
											label-for="versionId"
											label-class="required"
											:label-cols="4"
											:horizontal="true">
											<b-form-select v-model="form.versionId" name="versionId" id="versionId" :plain="true"
												:options="versionDropDownList"
												:state="$v.form.versionId.$dirty ? !$v.form.versionId.$error : null"
												aria-describedby="version-feedback"></b-form-select>
											<b-form-invalid-feedback id="version-feedback">Please select one.</b-form-invalid-feedback>
										</b-form-group>
									</b-col>
									<b-col cols="12" md="6">
										<b-form-group label="Employee limit" label-for="totalEmployeeLimit" :label-cols="4" :horizontal="true">
											<b-form-input v-model="form.totalEmployeeLimit" name="totalEmployeeLimit" id="totalEmployeeLimit"
												type="number" min="1" placeholder="25"></b-form-input>
										</b-form-group>
									</b-col>
								</b-row>
							</b-card>

							<!-- ── Advanced (office only) ─────────────────────────── -->
							<b-card v-if="createsOffice" no-body class="mb-3">
								<b-card-header class="advanced-header" @click="advancedOpen = !advancedOpen">
									<i :class="advancedOpen ? 'fa fa-caret-down' : 'fa fa-caret-right'"></i>
									Advanced
									<small class="text-muted ml-2">
										shift · fiscal year · office type · channel · device limits · display text — all pre-filled
									</small>
								</b-card-header>
								<b-collapse v-model="advancedOpen">
									<b-card-body>
										<b-row>
											<b-col cols="12" md="6">
												<b-form-group label="Office type" :label-cols="5" :horizontal="true">
													<b-form-select v-model="form.officeType" :plain="true" :options="officeTypeDropDownList"></b-form-select>
												</b-form-group>
												<b-form-group label="Fiscal year start" :label-cols="5" :horizontal="true">
													<b-form-select v-model="form.fiscalYearId" :plain="true" :options="monthDropDownList"></b-form-select>
												</b-form-group>
												<b-form-group label="Sales channel" :label-cols="5" :horizontal="true">
													<b-form-select v-model="form.channelId" :plain="true" :options="channelList"></b-form-select>
												</b-form-group>
												<b-form-group label="Device limit" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.totalDeviceLimit" type="number" min="0" placeholder="2"></b-form-input>
												</b-form-group>
												<b-form-group label="Location tracking users" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.locationTrackingUserLimit" type="number" min="0" placeholder="0"></b-form-input>
												</b-form-group>
												<b-form-group label="Description" :label-cols="5" :horizontal="true">
													<b-form-textarea v-model="form.description" rows="2" max-rows="4" placeholder="Optional"></b-form-textarea>
												</b-form-group>
											</b-col>

											<b-col cols="12" md="6">
												<b-form-group label="Shift name" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.shiftName" type="text" placeholder="Regular"></b-form-input>
												</b-form-group>
												<b-form-group label="Start time" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.startTime" type="time"></b-form-input>
												</b-form-group>
												<b-form-group label="End time" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.endTime" type="time"></b-form-input>
												</b-form-group>
												<b-form-group label="Late count starts" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.lateCountStartTime" type="time"></b-form-input>
												</b-form-group>
												<b-form-group label="Early count ends" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.earlyCountEndTime" type="time"></b-form-input>
												</b-form-group>
												<b-form-group :label-cols="5" :horizontal="true">
													<b-form-checkbox v-model="form.isMidnightCrossing" switch>Shift crosses midnight</b-form-checkbox>
												</b-form-group>
												<b-form-group label="Primary Display Text" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.primaryDisplayText" type="text" maxlength="10"
														placeholder="Welcome"></b-form-input>
												</b-form-group>
												<b-form-group label="Secondary Display Text" :label-cols="5" :horizontal="true">
													<b-form-input v-model="form.secondaryDisplayText" type="text" maxlength="10"
														placeholder="admin"></b-form-input>
													<small class="form-text text-muted">Max 10 characters — shown on the attendance device.</small>
												</b-form-group>
											</b-col>
										</b-row>
									</b-card-body>
								</b-collapse>
							</b-card>

							<!-- ── Tipsoi Pay (office only) ───────────────────────── -->
							<b-card v-if="createsOffice" border-variant="info" class="mb-3">
								<div slot="header" class="d-flex align-items-center">
									<span class="font-weight-bold mr-3">Tipsoi Pay</span>
									<toggle-button
										v-model="form.createBillingProject"
										:labels="{checked: 'ON', unchecked: 'OFF'}"
										:width="60"
										:sync="true"
										@change="onBillingToggle"/>
								</div>

								<p v-if="!form.createBillingProject" class="mb-0 text-muted">
									Off — the client is created without a billing project. Link it later from Tipsoi Pay
									if needed. Onboarding never blocks on billing.
								</p>

								<div v-else>
									<b-form-group label="Item" label-for="billingItemId" label-class="required" :label-cols="3" :horizontal="true">
										<model-select
											id="billingItemId"
											:options="billingItemOptions"
											v-model="form.billingItemId"
											placeholder="Search & select an invoice item"
											@input="onBillingItemSelected">
										</model-select>
										<small class="form-text text-muted">
											<span v-if="billingItemsLoading">Loading items…</span>
											<span v-else-if="billingItemsError" class="text-danger">{{ billingItemsError }}</span>
											<span v-else>Required so the billing project does not show "Item: N/A".</span>
										</small>
									</b-form-group>
									<b-row>
										<b-col cols="12" md="6">
											<b-form-group label="Price" :label-cols="6" :horizontal="true">
												<b-form-input v-model.number="form.billingItemPrice" type="number" min="0" step="0.01" placeholder="Unit price"></b-form-input>
											</b-form-group>
										</b-col>
										<b-col cols="12" md="6">
											<b-form-group label="Quantity" :label-cols="6" :horizontal="true">
												<b-form-input v-model.number="form.billingItemQty" type="number" min="1" step="1" placeholder="Quantity"></b-form-input>
											</b-form-group>
										</b-col>
									</b-row>
								</div>
							</b-card>

							<div class="form-actions">
								<b-button v-if="$store.getters.canWrite" type="submit" variant="primary" :disabled="submitting">
									<b-spinner v-if="submitting" small></b-spinner>
									<i v-else class="fa fa-dot-circle-o"></i>
									{{ submitLabel }}
								</b-button>
								&nbsp;
								<b-button type="button" variant="danger" :disabled="submitting" @click="resetForm()">
									<i class="fa fa-ban"></i> Reset
								</b-button>
							</div>
						</b-form>
					</b-card>
				</b-col>
			</b-row>
		</div>
	</div>
</template>

<script>
import {required, requiredIf, email, minLength, sameAs} from 'vuelidate/lib/validators';
import {ModelSelect} from 'vue-search-select';
import {ToggleButton} from 'vue-js-toggle-button';
import onboardingService from '@/services/onboardingService';
import customerService from '@/services/customerService';
import officeService from '@/services/officeService';
import versionService from '@/services/versionService';
import salesChannelService from '@/services/salesChannelService';
import toastService from '@/services/toastService';

const EMAIL_CHECK_DEBOUNCE_MS = 400;

function blankForm() {
	return {
		// PROJECT_AND_OFFICE | PROJECT_ONLY | OFFICE_ON_EXISTING_PROJECT
		mode: 'PROJECT_AND_OFFICE',
		projectId: null,

		clientName: null,
		address: null,
		phone: null,

		adminName: null,
		adminEmail: null,
		adminPassword: null,
		confirmPassword: null,

		versionId: null,
		totalEmployeeLimit: 25,

		officeType: 1,
		fiscalYearId: 1,
		channelId: null,
		description: null,
		totalDeviceLimit: 2,
		locationTrackingUserLimit: 0,

		shiftName: 'Regular',
		startTime: '09:00',
		endTime: '18:00',
		lateCountStartTime: '09:15',
		earlyCountEndTime: '17:45',
		isMidnightCrossing: false,
		// Real values, not placeholders — the operator can see and edit what will be sent.
		// Same defaults the API quick-create path uses. Max 10 chars (device column width).
		primaryDisplayText: 'Welcome',
		secondaryDisplayText: 'admin',

		createBillingProject: true,
		billingItemId: null,
		billingItemPrice: null,
		billingItemQty: null
	};
}

export default {
	name: 'CreateClient',
	components: {ModelSelect, ToggleButton},

	data: function () {
		return {
			form: blankForm(),

			loading: false,
			submitting: false,
			advancedOpen: true,
			showPassword: false,

			// Result state — the form is replaced by the handover card once this is set.
			result: null,
			submitted: {},

			emailChecking: false,
			emailAvailable: false,
			emailTaken: false,
			emailCheckTimer: null,

			projectDropDownList: [],
			projectsLoading: false,
			projectsError: null,
			projectsLoaded: false,

			versionDropDownList: [{text: 'Please Select', value: null}],
			channelList: [{text: 'Default', value: null}],
			officeTypeDropDownList: [
				{text: 'Corporate', value: 1},
				{text: 'Factory', value: 2}
			],
			monthDropDownList: [
				{text: 'January', value: 1}, {text: 'February', value: 2}, {text: 'March', value: 3},
				{text: 'April', value: 4}, {text: 'May', value: 5}, {text: 'June', value: 6},
				{text: 'July', value: 7}, {text: 'August', value: 8}, {text: 'September', value: 9},
				{text: 'October', value: 10}, {text: 'November', value: 11}, {text: 'December', value: 12}
			],

			billingItemList: [],
			billingItemsLoading: false,
			billingItemsError: null
		};
	},

	validations: {
		form: {
			clientName: {required},
			address: {required},
			phone: {required},
			adminName: {required},
			adminEmail: {required, validEmail: email},
			adminPassword: {required, minLength: minLength(6)},
			confirmPassword: {required, sameAsPassword: sameAs('adminPassword')},
			versionId: {
				// Not needed when only a Device Portal project is being created.
				required: requiredIf(function (form) { return form.mode !== 'PROJECT_ONLY'; })
			},
			projectId: {
				// Only required when attaching to a project that already exists.
				required: requiredIf(function (form) { return form.mode === 'OFFICE_ON_EXISTING_PROJECT'; })
			}
		}
	},

	computed: {
		/** Two of the three modes create a Tipsoi office; project-only does not. */
		createsOffice: function () {
			return this.form.mode !== 'PROJECT_ONLY';
		},
		/** Two of the three modes create a new Device Portal project. */
		createsPortalProject: function () {
			return this.form.mode !== 'OFFICE_ON_EXISTING_PROJECT';
		},
		submitLabel: function () {
			if (this.submitting) return 'Creating…';
			return this.form.mode === 'PROJECT_ONLY' ? 'Create Project' : 'Create Client';
		},
		emailState: function () {
			if (this.emailTaken) return false;
			if (!this.$v.form.adminEmail.$dirty) return null;
			return !this.$v.form.adminEmail.$error;
		},
		billingItemOptions: function () {
			return this.billingItemList.map(i => ({
				text: i.name + (i.selling_price ? ('  —  BDT ' + Number(i.selling_price).toFixed(2)) : ''),
				value: i.id
			}));
		}
	},

	methods: {
		// ── Mode ─────────────────────────────────────────────────────────────
		onModeChange: function (mode) {
			if (mode === 'OFFICE_ON_EXISTING_PROJECT') {
				// The project list is a live Device Portal round-trip, so it is fetched only when a
				// mode actually needs it rather than on every mount.
				this.loadProjectNames();
			} else {
				this.form.projectId = null;
			}
		},

		onBillingToggle: function () {
			if (this.form.createBillingProject) {
				this.loadBillingItems();
			}
		},

		// ── Email availability ───────────────────────────────────────────────
		onEmailInput: function () {
			this.emailAvailable = false;
			this.emailTaken = false;
			if (this.emailCheckTimer) clearTimeout(this.emailCheckTimer);

			const value = this.form.adminEmail;
			if (!value || !/.+@.+\..+/.test(value)) return;

			this.emailCheckTimer = setTimeout(() => {
				this.emailChecking = true;
				onboardingService.checkEmailAvailable(value, (available) => {
					// Ignore a response that arrived after the field changed again.
					if (this.form.adminEmail !== value) { this.emailChecking = false; return; }
					this.emailChecking = false;
					this.emailAvailable = available;
					this.emailTaken = !available;
				}, () => {
					// A failed check must not block submission — the server re-checks anyway.
					this.emailChecking = false;
				});
			}, EMAIL_CHECK_DEBOUNCE_MS);
		},

		generatePassword: function () {
			const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
			const lower = 'abcdefghijkmnopqrstuvwxyz';
			const digits = '23456789';
			const symbols = '@#$%&*';
			const all = upper + lower + digits + symbols;
			const pick = (set) => set.charAt(Math.floor(Math.random() * set.length));

			// Guarantee one of each class, then fill to 12 and shuffle.
			let chars = [pick(upper), pick(lower), pick(digits), pick(symbols)];
			while (chars.length < 12) chars.push(pick(all));
			for (let i = chars.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1));
				const tmp = chars[i]; chars[i] = chars[j]; chars[j] = tmp;
			}

			const password = chars.join('');
			this.form.adminPassword = password;
			this.form.confirmPassword = password;
			this.showPassword = true;
		},

		// ── Submit ───────────────────────────────────────────────────────────
		checkFormValidity: function () {
			this.$v.form.$touch();
			if (this.$v.form.$anyError) return;

			// The local email check only reflects Tipsoi tables, which project-only never touches —
			// the Device Portal enforces its own uniqueness there and reports it clearly.
			if (this.emailTaken && this.createsOffice) {
				toastService.showErrorToast('That email is already in use — choose another.');
				return;
			}
			if (this.createsOffice && this.form.createBillingProject && !this.form.billingItemId) {
				toastService.showErrorToast('Please select a Tipsoi Pay item, or switch Tipsoi Pay off.');
				return;
			}
			this.createClient();
		},

		createClient: function () {
			this.submitting = true;
			const billingOn = this.createsOffice && this.form.createBillingProject;

			const payload = {
				clientName: this.form.clientName,
				address: this.form.address,
				phone: this.form.phone,

				adminName: this.form.adminName,
				adminEmail: this.form.adminEmail,
				adminPassword: this.form.adminPassword,

				mode: this.form.mode,
				versionId: this.createsOffice ? this.form.versionId : null,
				projectId: this.form.mode === 'OFFICE_ON_EXISTING_PROJECT' ? this.form.projectId : null,

				officeType: this.form.officeType,
				fiscalYearId: this.form.fiscalYearId,
				channelId: this.form.channelId,
				description: this.form.description,
				totalEmployeeLimit: toStringOrNull(this.form.totalEmployeeLimit),
				totalDeviceLimit: toStringOrNull(this.form.totalDeviceLimit),
				locationTrackingUserLimit: toStringOrNull(this.form.locationTrackingUserLimit),

				// Sent as 24-hour HH:mm — the server converts. Deliberately NOT converted here:
				// the older page's converter turns 12:00 into "00:00 PM", which the server rejects.
				shiftName: this.form.shiftName,
				startTime: this.form.startTime,
				endTime: this.form.endTime,
				lateCountStartTime: this.form.lateCountStartTime,
				earlyCountEndTime: this.form.earlyCountEndTime,
				isMidnightCrossing: this.form.isMidnightCrossing,

				primaryDisplayText: this.form.primaryDisplayText,
				secondaryDisplayText: this.form.secondaryDisplayText,
				sendEmail: false,

				createBillingProject: this.createsOffice && this.form.createBillingProject,
				billingItemId: billingOn ? this.form.billingItemId : null,
				billingItemPrice: billingOn ? nullIfEmpty(this.form.billingItemPrice) : null,
				billingItemQty: billingOn ? nullIfEmpty(this.form.billingItemQty) : null
			};

			// Held locally so the handover card can show the password — it is never returned by the API.
			const submitted = {
				clientName: this.form.clientName,
				adminPassword: this.form.adminPassword
			};

			onboardingService.createClient(payload, (data) => {
				this.submitting = false;
				this.submitted = submitted;
				this.showPassword = false;
				this.result = Object.assign({warnings: []}, data);
				this.$nextTick(() => { this.$v.$reset(); });
				toastService.showSuccessToast('Client created');
			}, (error) => {
				this.submitting = false;
				toastService.showErrorToast(error);
			});
		},

		startAnother: function () {
			this.result = null;
			this.submitted = {};
			this.resetForm();
		},

		resetForm: function () {
			this.form = blankForm();
			this.emailAvailable = false;
			this.emailTaken = false;
			this.emailChecking = false;
			this.showPassword = false;
			this.advancedOpen = true;
			this.$nextTick(() => { this.$v.$reset(); });
		},

		// ── Reference data ───────────────────────────────────────────────────
		loadProjectNames: function () {
			if (this.projectsLoaded) return;
			this.projectsLoading = true;
			this.projectsError = null;
			customerService.getProjectNames((data) => {
				this.projectDropDownList = (data || []).map(p => ({text: p.name, value: p.id}));
				this.projectsLoading = false;
				this.projectsLoaded = true;
			}, (error) => {
				this.projectsLoading = false;
				this.projectsError = error || 'Could not load projects';
			});
		},

		loadVersions: function () {
			versionService.getVersionList((data) => {
				this.versionDropDownList = [{text: 'Please Select', value: null}]
					.concat((data || []).map(v => ({text: v.name, value: v.id})));
			}, (error) => toastService.showErrorToast('Could not load versions: ' + error));
		},

		loadSalesChannels: function () {
			// The error callback is not optional: salesChannelService calls error(...) on failure,
			// so omitting it (as CreateOffice.vue does) invokes undefined and throws during
			// created() — taking the whole page down because a dropdown could not load.
			salesChannelService.getSalesChannelList((data) => {
				this.channelList = [{text: 'Default', value: null}]
					.concat((data || []).map(d => ({text: d.name, value: d.name})));
			}, () => {
				// Channel is optional — the server defaults it. Leave the list as-is.
			});
		},

		loadBillingItems: function () {
			if (this.billingItemList.length > 0) return;
			this.billingItemsLoading = true;
			this.billingItemsError = null;
			officeService.getBillingItems((items) => {
				this.billingItemList = Array.isArray(items) ? items : [];
				this.billingItemsLoading = false;
			}, (err) => {
				this.billingItemList = [];
				this.billingItemsError = err || 'Could not load Tipsoi Pay items';
				this.billingItemsLoading = false;
			});
		},

		onBillingItemSelected: function (value) {
			const id = (value && typeof value === 'object') ? value.value : value;
			const item = this.billingItemList.find(i => i.id === id);
			if (item) {
				this.form.billingItemPrice = Number(item.selling_price) || 0;
				if (!this.form.billingItemQty || this.form.billingItemQty < 1) {
					this.form.billingItemQty = parseInt(this.form.totalEmployeeLimit) || 1;
				}
			}
		},

		// ── Handover ─────────────────────────────────────────────────────────
		copyHandover: function () {
			const lines = [
				'Client: ' + this.submitted.clientName,
				this.result.portalProjectCreated
					? 'Device Portal project: ' + this.result.projectCode + ' (id ' + this.result.projectId + ')'
					: 'Device Portal project id: ' + this.result.projectId
			];
			if (this.result.officeCreated !== false) {
				lines.push('Tipsoi office id: ' + this.result.officeId);
			}
			lines.push(
				'Login email: ' + this.result.adminEmail,
				'Password: ' + this.submitted.adminPassword,
				'Tipsoi Pay: ' + (this.result.billingStatus || 'Not requested')
			);
			copyToClipboard(lines.join('\n'),
				() => toastService.showSuccessToast('Handover details copied'),
				() => toastService.showErrorToast('Could not copy — select the text manually'));
		}
	},

	created() {
		this.loadVersions();
		this.loadSalesChannels();
		// Tipsoi Pay is on by default, so its item list is needed immediately — an item is required
		// before the form will submit. The project list stays on-demand (see onModeChange): it is
		// a live Device Portal round-trip that the default mode never needs.
		this.loadBillingItems();
	},

	beforeDestroy() {
		if (this.emailCheckTimer) clearTimeout(this.emailCheckTimer);
	}
};

function toStringOrNull(value) {
	if (value === null || value === undefined || value === '') return null;
	return String(value);
}

function nullIfEmpty(value) {
	return (value === null || value === undefined || value === '') ? null : value;
}

function copyToClipboard(text, onSuccess, onFailure) {
	if (navigator.clipboard && navigator.clipboard.writeText) {
		navigator.clipboard.writeText(text).then(onSuccess).catch(onFailure);
		return;
	}
	// Fallback for non-secure origins, where navigator.clipboard is undefined.
	try {
		const area = document.createElement('textarea');
		area.value = text;
		area.style.position = 'fixed';
		area.style.opacity = '0';
		document.body.appendChild(area);
		area.select();
		const ok = document.execCommand('copy');
		document.body.removeChild(area);
		ok ? onSuccess() : onFailure();
	} catch (e) {
		onFailure();
	}
}
</script>

<style src="vue-search-select/dist/VueSearchSelect.css"></style>
<style scoped>
.advanced-header {
	cursor: pointer;
	user-select: none;
}

.handover-table th {
	width: 11rem;
	font-weight: 600;
	border-top: none;
}

.handover-table td {
	border-top: none;
}

.handover-table code {
	font-size: 1rem;
}
</style>
